import { FC, useCallback, useEffect, useState } from 'react';
import { Alert, Grid, LinearProgress, Snackbar } from '@mui/material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import { AddButton } from '../../../../components/simple/AllButtons';
import { TStaffResponse } from '../../../../../apis/staff/staff.interfaces';
import {
  getColumnsForStaffGrid,
  getStaffGridRowsToRender,
} from './staffList.helpers';
import { useStaffActions, useStaffState } from '../../contexts/staff.context';
import StaffManageDialog, { StaffFormData, getUserContext } from './staffManageDialog.view';
import { useStaffService } from '../../../../../apis/staff/useStaffService';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { PermissionGate } from '../../../../../components/rbac/PermissionGate';
import { useQueryClient } from '@tanstack/react-query';
import { getStaffListKey } from '../../hooks/useStaffListHook';
import { useStaffEligibleUsersHook } from '../../hooks/useStaffEligibleUsersHook';

const StaffList: FC = () => {
  const {
    isLoading,
    paginationParams,
    totalCount,
    allStaff,
    selectedStaffId,
    selectedStaff,
  } = useStaffState();
  const { updatePaginationParams, setSelectedStaffId, setSelectedStaff } =
    useStaffActions();
  const { getStaffById, createStaff, updateStaff, deleteStaff } = useStaffService();
  const { translateText } = useTranslation();
  const queryClient = useQueryClient();

  const [openAddStaffDialog, setOpenAddStaffDialog] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const { instituteId, userId: loggedInUserId } = getUserContext();

  // Fetch eligible users to resolve addressId/contactId on create
  const { data: eligibleUsersResponse } = useStaffEligibleUsersHook(instituteId);

  const onRowClickHandler = (data: TStaffResponse) => {
    setSelectedStaffId(data.id);
    setOpenAddStaffDialog(true);
  };

  const onAddStaffClickHandler = () => {
    setSelectedStaffId(null);
    setOpenAddStaffDialog(true);
  };

  useEffect(() => {
    if (selectedStaffId) {
      getStaffById(selectedStaffId)
        .then((staff) => {
          setSelectedStaff(staff);
        })
        .catch((error) => {
          console.error('Error fetching staff details:', error);
        });
    } else {
      setSelectedStaff(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaffId]);

  const handleSubmitForm = useCallback(
    async (data: StaffFormData, isUpdate: boolean) => {
      setFormError('');
      try {
        if (isUpdate && selectedStaffId) {
          await updateStaff(selectedStaffId, {
            designation: data.designation,
            department: data.department,
            joiningDate: data.joiningDate,
            employmentStatus: data.employmentStatus === 'select' ? undefined : data.employmentStatus,
            updatedBy: loggedInUserId,
          });
        } else {
          // Resolve addressId and contactId from selected user
          const selectedUser = eligibleUsersResponse?.data?.find(
            (u) => u.id === data.userId
          );

          await createStaff({
            userId: data.userId,
            instituteId,
            supervisorId: data.supervisorId || '',
            documents: [],
            designation: data.designation,
            department: data.department,
            joiningDate: data.joiningDate,
            employmentStatus: data.employmentStatus === 'select' ? 'active' : data.employmentStatus,
            addressId: selectedUser?.addressId || '',
            contactId: selectedUser?.contactId || '',
            createdBy: loggedInUserId,
          });
        }
        // Invalidate staff list to refresh
        queryClient.invalidateQueries({ queryKey: [getStaffListKey] });
        setSuccessMessage(isUpdate ? 'Staff member updated successfully.' : 'Staff member created successfully.');
        setOpenAddStaffDialog(false);
      } catch (error: any) {
        setFormError(error?.message || 'An error occurred');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedStaffId, instituteId, loggedInUserId, eligibleUsersResponse]
  );

  const handleDeleteStaff = useCallback(
    async (data: TStaffResponse) => {
      if (!window.confirm(translateText('staff.list.confirmDelete') || 'Are you sure you want to delete this staff member?')) {
        return;
      }
      try {
        await deleteStaff(data.id);
        queryClient.invalidateQueries({ queryKey: [getStaffListKey] });
        setSuccessMessage('Staff member deleted successfully.');
      } catch (error: any) {
        console.error('Error deleting staff:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteStaff]
  );

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <Grid container spacing={{ xs: 1, sm: 2 }}>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
          gap: { xs: 1, sm: 2 },
        }}
        data-testid="staff-list-header"
      >
        <PermissionGate module="staff" submodule="staffList" permission="write">
          <AddButton
            label={translateText('staff.list.addStaff')}
            onClickHandler={onAddStaffClickHandler}
            data-testid="staff-list-add-button"
          />
        </PermissionGate>
      </Grid>
      <Grid item xs={12}>
        <PaginatedGrid
          rows={getStaffGridRowsToRender(allStaff)}
          columns={getColumnsForStaffGrid(onRowClickHandler, translateText, handleDeleteStaff)}
          dataTestId="staff-profiles-grid"
          serverSide={true}
          totalCount={totalCount}
          currentPage={paginationParams.page}
          currentPageSize={paginationParams.limit}
          onPageChange={(page, limit) =>
            updatePaginationParams({ page, limit })
          }
          hideColumnsOnMobile={['id', 'email', 'phone', 'department']}
        />
      </Grid>
      <StaffManageDialog
        isOpen={openAddStaffDialog}
        onClose={() => { setOpenAddStaffDialog(false); setFormError(''); }}
        errorMessage={formError}
        staffDetails={selectedStaff}
        isLoading={false}
        onSubmitForm={handleSubmitForm}
      />
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default StaffList;
